/**
 * Booking Page
 *
 * Main booking interface with:
 * - Left panel: Booking type toggle, filters, date picker (or SelectionToolSidebar in edit mode)
 * - Right panel: Schedule grid with week/month/year views
 *
 * The panel switches between filter content and SelectionToolSidebar based on edit mode.
 * EditModeButton is rendered within the Schedule component.
 *
 * Ported from Vue views/booking/Home.vue
 */

import { ActionButton } from "@/components/ui/buttons/ActionButton";
import { Button } from "@/components/ui/buttons/Button";
import { ButtonTabs } from "@/components/ui/buttons/ButtonTabs";
import { Icon } from "@/components/ui/display/Icon";
import { Toggle } from "@/components/ui/forms/Toggle";
import { FlashAlert } from "@/components/ui/feedback/FlashAlert";
import { Modal } from "@/components/ui/feedback/Modal";
import { DatePicker } from "@/components/ui/forms/DatePicker";
import type { BookingUpdate } from "@/components/booking/forms/EditBookingModal/types";
import type { EditMode } from "@/components/booking/forms/BookingFormWrapper/types";
import { CustomerEmployeeSelector } from "@/components/ui/forms/CustomerEmployeeSelector";
import { EmployeeProjectSelector } from "@/components/ui/forms/EmployeeProjectSelector";
import { PanelSplitView } from "@/components/layout/PanelSplitView";
import { BottomToolbar } from "@/components/ui/navigation/BottomToolbar";
import { Schedule } from "@/components/schedule/Schedule";
import { SelectedBookingSidebar } from "@/components/layout/SelectedBookingSidebar";
import type { EditFormMode } from "@/components/layout/SelectionToolSidebar";
import { SelectionToolSidebar } from "@/components/layout/SelectionToolSidebar";
import {
  ErrorBoundary,
  FeatureErrorFallback,
} from "@/components/shared/ErrorBoundary";
import { useBookingFilters } from "@/hooks/booking/useBookingFilters";
import { useBookingMutations } from "@/hooks/booking/useBookingMutations";
import { useBookingSelection } from "@/hooks/booking/useBookingSelection";
import { useBookingUiState } from "@/hooks/booking/useBookingUiState";
import { useBookingView } from "@/hooks/booking/useBookingView";
import { useProjects } from "@/hooks/data/useProjects";
import { useCustomers } from "@/hooks/data/useCustomers";
import { formatEmployeeName } from "@/hooks/data/dataQuality";
import type {
  BookingViewMode,
  IBooking,
  IServiceCustomer,
  IServiceEmployee,
  IServiceProject,
} from "@/services/types";
import { BookingType } from "@/services/types";
import { disableFravaerAtom, hoursInDayAtom } from "@/store/userStore";
import { useAtomValue } from "jotai";
import React, { lazy, Suspense, useEffect, useState } from "react";
import styles from "./BookingPage.module.css";
import type { SelectedCardData } from "@/store/bookingStore";

// Lazy-loaded modals for better initial bundle size
// These are only loaded when the user triggers them
const HelpModal = lazy(() =>
  import("@/components/ui/feedback/HelpModal").then((m) => ({
    default: m.HelpModal,
  })),
);
const ConfirmDialog = lazy(() =>
  import("@/components/ui/feedback/ConfirmDialog").then((m) => ({
    default: m.ConfirmDialog,
  })),
);
const EditBookingModal = lazy(() =>
  import("@/components/booking/forms/EditBookingModal").then((m) => ({
    default: m.EditBookingModal,
  })),
);
const BookingFormWrapper = lazy(() =>
  import("@/components/booking/forms/BookingFormWrapper").then((m) => ({
    default: m.BookingFormWrapper,
  })),
);

// View mode options for ButtonTabs
const VIEW_MODE_OPTIONS = [
  { id: "week" as const, label: "Uke" },
  { id: "month" as const, label: "Måned" },
  { id: "year" as const, label: "År" },
];

export const BookingPage: React.FC = () => {
  // Organization settings
  const absenceDisabled = useAtomValue(disableFravaerAtom);
  const hoursInDay = useAtomValue(hoursInDayAtom);

  // Booking filters and view state
  const {
    bookingType,
    setBookingType,
    viewMode,
    changeViewMode,
    hideWeekend,
    setHideWeekend,
    clearFilters,
    employees,
    projects,
    customers,
    setEmployees,
    setProjects,
    setCustomers,
    dateRange,
    goToPrevious,
    goToNext,
    goToToday,
    setFocusDate,
  } = useBookingFilters();

  // useBookingView provides additional view calculations if needed
  useBookingView();

  // Booking mutations for edit/delete
  const { updateBooking, deleteBooking } = useBookingMutations();

  // Projects and customers for edit modal
  const { activeProjects } = useProjects();
  const { activeCustomers } = useCustomers();

  // Centralized UI state (side panel, selected card, help modal)
  const {
    selectedCard,
    setSelectedCard,
    panelCollapsed,
    setPanelCollapsed,
    showHelpModal,
    setShowHelpModal,
    selectMode,
    panelVariant,
    closeSelectedCard,
  } = useBookingUiState();

  // Selection state for bulk operations
  const { selectedCards, exitSelectMode } = useBookingSelection();

  /**
   * Compute the target entity from selected cards.
   * If all selected cards belong to a single customer, return that customer.
   * If all selected cards belong to a single employee, return that employee.
   * Otherwise return null.
   *
   * This determines whether the AddForm shows a simplified view (customer target)
   * or the full form with selector dropdowns.
   */
  const selectedTargetEntity: IServiceCustomer | IServiceEmployee | null =
    (() => {
      if (selectedCards.length === 0) return null;

      // Check if all cards have the same customer
      const customerIds = selectedCards
        .map((c) => c.customer?.id)
        .filter(Boolean);
      const uniqueCustomers = Array.from(new Set(customerIds));
      if (uniqueCustomers.length === 1 && selectedCards[0].customer) {
        return selectedCards[0].customer;
      }

      // Check if all cards have the same employee
      const employeeIds = selectedCards
        .map((c) => c.employee?.id)
        .filter(Boolean);
      const uniqueEmployees = Array.from(new Set(employeeIds));
      if (uniqueEmployees.length === 1 && selectedCards[0].employee) {
        return selectedCards[0].employee;
      }

      return null;
    })();

  // Local UI state
  const [editingBooking, setEditingBooking] = useState<IBooking | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<IBooking | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Bulk form modal state
  const [bulkFormMode, setBulkFormMode] = useState<EditMode | null>(null);
  const [isBulkFormModalOpen, setIsBulkFormModalOpen] = useState(false);

  // Alert messages
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /*
    Why all the callback where removed:
    We are not memoizing the subcomponents that are using these handler functions and
    it does not make sense to do so either at this time.
  */

  // Handle form action from SelectionToolSidebar (Add, Edit, Delete buttons)
  const handleFormAction = (mode: EditFormMode) => {
    setBulkFormMode(mode);
    setIsBulkFormModalOpen(true);
  };

  // Handle bulk form close
  const handleBulkFormClose = () => {
    setIsBulkFormModalOpen(false);
    setBulkFormMode(null);
  };

  // Handle bulk form done (success)
  const handleBulkFormDone = (message: string) => {
    setSuccessMessage(message);
    handleBulkFormClose();
    // Exit select mode after successful operation
    exitSelectMode();
  };

  // Handle bulk form error
  const handleBulkFormError = (message: string) => {
    setErrorMessage(message);
  };

  // Get modal title based on mode
  const getBulkFormModalTitle = (): string => {
    // Get employee name from first selected card (if available)
    const employee = selectedCards[0]?.employee;
    const employeeName = employee ? formatEmployeeName(employee) : null;

    switch (bulkFormMode) {
      case "add":
        return "Legg til booking";
      case "edit":
        return employeeName
          ? `Rediger booking for ${employeeName}`
          : "Rediger bookinger";
      case "delete":
        return "Slett bookinger";
      default:
        return "";
    }
  };

  // Handle card click - show booking details sidebar
  const handleCloseSelectedCard = () => {
    closeSelectedCard();
  };

  // Handle selecting an employee from sidebar - filter to that employee
  // Keep panel open but clear selected card to show filter panel
  const handleSelectEmployee = (employee: IServiceEmployee) => {
    setBookingType(BookingType.HARD_BOOKING);
    setEmployees([employee]);
    setCustomers([]);
    setSelectedCard(null); // Clear card but keep panel open
  };

  // Handle selecting a project from sidebar - filter to that project
  // Keep panel open but clear selected card to show filter panel
  const handleSelectProject = (project: IServiceProject) => {
    setBookingType(BookingType.HARD_BOOKING);
    setProjects([project]);
    setSelectedCard(null); // Clear card but keep panel open
  };

  // Handle selecting a customer from sidebar - filter to that customer
  // Keep panel open but clear selected card to show filter panel
  const handleSelectCustomer = (customer: IServiceCustomer) => {
    setBookingType(BookingType.SOFT_BOOKING);
    setCustomers([customer]);
    setEmployees([]);
    setSelectedCard(null); // Clear card but keep panel open
  };

  const handleOnPanelCollapse = (collapsed?: boolean) => {
    const newPanelState = collapsed !== undefined ? collapsed : !panelCollapsed;

    setPanelCollapsed(newPanelState);
  };

  // Handle edit booking - open edit modal
  const handleEditBooking = (booking: IBooking) => {
    setEditingBooking(booking);
    setIsEditModalOpen(true);
  };

  // Handle save booking from edit modal
  const handleSaveBooking = async (updates: BookingUpdate) => {
    if (!editingBooking || !selectedCard) return;

    // Determine booking type and IDs based on the update type:
    // - Absence: ABSENCE type, no project/customer
    // - Soft booking: SOFT_BOOKING type, customer from updates
    // - Hard booking: HARD_BOOKING type, project from updates
    let bookingType: BookingType;
    let projectId: number | null = null;
    let customerId: number | null = null;

    if (updates.isAbsence) {
      bookingType = BookingType.ABSENCE;
    } else if (updates.isSoftBooking) {
      bookingType = BookingType.SOFT_BOOKING;
      customerId = updates.customerId;
    } else {
      bookingType = BookingType.HARD_BOOKING;
      projectId = updates.projectId;
    }

    // Returns the updated booking (and invalidates/refetches internally)
    const updatedBooking = await updateBooking({
      bookingId: editingBooking.id,
      projectId,
      hours: updates.hours,
      date: selectedCard.date,
      employeeId: editingBooking.employee?.id ?? null,
      customerId,
      bookingType,
    });

    // Update selectedCard snapshot so sidebar refreshes immediately
    setSelectedCard((prev) => {
      if (!prev) return prev;

      const replace = (list: IBooking[]) =>
        list.map((b) => (b.id === updatedBooking.id ? updatedBooking : b));

      const next: SelectedCardData = {
        ...prev,
        bookings: replace(prev.bookings),
        excludedBookings: replace(prev.excludedBookings),
        // keep card.customer in sync with the booking(s) shown in the sidebar
        customer: updatedBooking.customer ?? prev.customer,
        // employee/date/id unchanged
      };

      return next;
    });

    handleCloseEditModal();
  };

  // Handle close edit modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingBooking(null);
  };

  // Handle delete booking - show confirmation dialog
  const handleDeleteBooking = (booking: IBooking) => {
    setBookingToDelete(booking);
    setIsDeleteDialogOpen(true);
  };

  // Confirm delete booking
  const handleConfirmDelete = async () => {
    if (!bookingToDelete) return;

    try {
      const deletedId = bookingToDelete.id;

      await deleteBooking(bookingToDelete);

      // Keep sidebar open: update selectedCard by removing deleted booking
      setSelectedCard((prev) => {
        if (!prev) return prev;

        const bookings = prev.bookings.filter((b) => b.id !== deletedId);
        const excludedBookings = prev.excludedBookings.filter(
          (b) => b.id !== deletedId,
        );

        // Close only when empty
        if (bookings.length === 0 && excludedBookings.length === 0) {
          return null; // closes sidebar
        }

        return {
          ...prev,
          bookings,
          excludedBookings,
        };
      });

      setSuccessMessage("Booking slettet");
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Kunne ikke slette booking. Prøv igjen senere.",
      );
    } finally {
      setIsDeleteDialogOpen(false);
      setBookingToDelete(null);
    }
  };

  // Cancel delete booking
  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setBookingToDelete(null);
  };

  // Clear selected card when entering select mode
  useEffect(() => {
    if (selectMode) {
      setSelectedCard(null);
    }
  }, [selectMode, setSelectedCard]);

  // Handle booking type change
  const handleBookingTypeChange = (
    type: typeof BookingType.HARD_BOOKING | typeof BookingType.SOFT_BOOKING,
  ) => {
    setBookingType(type);
    clearFilters();
  };

  // Handle view mode change
  const handleViewModeChange = (modeId: string, focusDate?: Date) => {
    changeViewMode(modeId as BookingViewMode, focusDate);
  };

  // Handle weekend toggle
  const handleWeekendToggle = (value: boolean) => {
    setHideWeekend(!value); // Toggle is "show weekend", store is "hide weekend"
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLInputElement;

      // Don't trigger shortcuts when typing in text fields
      if (target.type === "text" || target.tagName === "INPUT") {
        return;
      }

      if (event.key === "?") {
        setShowHelpModal((prev) => !prev);
      } else if (event.key === "Escape" && showHelpModal) {
        setShowHelpModal(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showHelpModal, setShowHelpModal]);

  // Filter panel content (left sidebar - filters and date picker)
  const filterPanelContent = (
    <div className={styles.sidebarContent}>
      <h3 className={styles.sidebarTitle}>Bookingoversikt</h3>

      {/* Booking type toggle buttons */}
      <div className={styles.bookingTypeToggle}>
        <ActionButton
          label="Hard booking"
          selected={bookingType === BookingType.HARD_BOOKING}
          variant="secondary"
          hoverVariant="info"
          onClick={() => handleBookingTypeChange(BookingType.HARD_BOOKING)}
          data-testid="booking-type-hard"
        />
        <ActionButton
          label="Soft booking"
          selected={bookingType === BookingType.SOFT_BOOKING}
          variant="secondary"
          hoverVariant="info"
          onClick={() => handleBookingTypeChange(BookingType.SOFT_BOOKING)}
          data-testid="booking-type-soft"
        />
      </div>

      {/* Employee/Project Selector for Hard Booking */}
      {bookingType === BookingType.HARD_BOOKING ? (
        <EmployeeProjectSelector
          selectedEmployees={employees}
          selectedProjects={projects}
          onSelectEmployees={(emps: IServiceEmployee[]) => setEmployees(emps)}
          onSelectProjects={(projs: IServiceProject[]) => setProjects(projs)}
          data-testid="employee-project-selector"
        />
      ) : (
        <CustomerEmployeeSelector
          defaultLoggedIn={false}
          selectedEmployees={employees}
          selectedCustomers={customers}
          onSelectEmployees={(emps: IServiceEmployee[]) => setEmployees(emps)}
          onSelectCustomers={(custs: IServiceCustomer[]) => setCustomers(custs)}
          data-testid="customer-employee-selector"
        />
      )}

      {/* DatePicker */}
      <DatePicker
        viewMode={viewMode}
        startDate={dateRange.start}
        endDate={dateRange.end}
        onDateSelect={setFocusDate}
        onPrevious={goToPrevious}
        onNext={goToNext}
        onToday={goToToday}
        size="md"
        data-testid="booking-date-picker"
      />
    </div>
  );

  // Panel content - show SelectedBookingSidebar when card selected, SelectionToolSidebar in edit mode, filters otherwise
  const getPanelContent = () => {
    if (selectedCard) {
      return (
        <SelectedBookingSidebar
          card={selectedCard}
          hoursInDay={hoursInDay}
          onClose={handleCloseSelectedCard}
          onEditBooking={handleEditBooking}
          onDeleteBooking={handleDeleteBooking}
          onSelectEmployee={handleSelectEmployee}
          onSelectProject={handleSelectProject}
          onSelectCustomer={handleSelectCustomer}
          data-testid="selected-booking-sidebar"
        />
      );
    }
    if (selectMode) {
      return (
        <SelectionToolSidebar
          onAction={handleFormAction}
          showWeekends={!hideWeekend}
          data-testid="selection-tool-sidebar"
        />
      );
    }
    return filterPanelContent;
  };
  const panelContent = getPanelContent();

  // Calculate the content offset for the overlay toolbar
  // When expanded: panel content width (25em = 24em base + 1em for DatePicker fit)
  // When collapsed: 0 (toolbar starts from left edge)
  const toolbarContentOffset = panelCollapsed ? "0" : "25em";

  const isCustomerOnlyContext =
    bookingType === BookingType.SOFT_BOOKING &&
    customers.length === 1 &&
    employees.length === 0;

  return (
    <div className={styles.bookingPage} data-testid="booking-page">
      <PanelSplitView
        variant={panelVariant}
        panel={panelContent}
        data-testid="booking-panel-split"
        collapsed={panelCollapsed}
        onCollapse={handleOnPanelCollapse}
        onClose={selectedCard ? handleCloseSelectedCard : undefined}
      >
        {/* Main content area */}
        <div className={styles.contentArea}>
          {/* Schedule grid - wrapped in ErrorBoundary for isolation */}
          <ErrorBoundary
            FallbackComponent={(props) => (
              <FeatureErrorFallback
                {...props}
                title="Kunne ikke laste kalenderen"
                message="En feil oppstod ved lasting av kalenderen. Prøv å laste siden på nytt."
              />
            )}
          >
            <Schedule
              viewMode={viewMode}
              onViewModeChange={changeViewMode}
              employees={employees}
              projects={projects}
              customers={customers}
              startDate={dateRange.start}
              endDate={dateRange.end}
              hideWeekend={hideWeekend}
              className={styles.scheduleArea}
              data-testid="booking-schedule"
            />
          </ErrorBoundary>
        </div>
      </PanelSplitView>

      {/* Bottom toolbar - overlay mode spans full width */}
      <BottomToolbar
        overlay
        contentOffset={toolbarContentOffset}
        aria-label="Kalenderverktøy"
        data-testid="booking-bottom-toolbar"
      >
        <ButtonTabs
          options={VIEW_MODE_OPTIONS}
          selected={viewMode}
          onSelect={handleViewModeChange}
          data-testid="schedule-view-mode-toggle"
        />

        <Toggle
          textOff="Ikke vis helg"
          textOn="Vis helg"
          checked={!hideWeekend}
          onChange={handleWeekendToggle}
          responsiveCompact
          compactLabel="Helg"
          data-testid="schedule-weekend-toggle"
        />

        <Button
          variant="ghost"
          size="sm"
          className={styles.helpButton}
          onClick={() => setShowHelpModal(true)}
          aria-label="Hjelp"
        >
          <Icon name="help-circle" size="sm" />
          <span className={styles.helpButtonText}>Hjelp</span>
        </Button>
      </BottomToolbar>

      {/* Help Modal - lazy loaded */}
      {showHelpModal && (
        <Suspense fallback={null}>
          <HelpModal
            isOpen={showHelpModal}
            title="Hjelp"
            onClose={() => setShowHelpModal(false)}
          />
        </Suspense>
      )}

      {/* Edit Booking Modal - lazy loaded, wrapped in ErrorBoundary */}
      {editingBooking && selectedCard && (
        <ErrorBoundary
          FallbackComponent={(props) => (
            <FeatureErrorFallback
              {...props}
              title="Kunne ikke laste redigeringsskjema"
              message="En feil oppstod. Lukk og prøv igjen."
            />
          )}
          onReset={handleCloseEditModal}
        >
          <Suspense fallback={null}>
            <EditBookingModal
              isOpen={isEditModalOpen}
              booking={editingBooking}
              date={selectedCard.date}
              projects={activeProjects}
              customers={activeCustomers}
              hoursInDay={hoursInDay}
              isCustomerTarget={isCustomerOnlyContext}
              onSave={handleSaveBooking}
              onClose={handleCloseEditModal}
              data-testid="edit-booking-modal"
            />
          </Suspense>
        </ErrorBoundary>
      )}

      {/* Delete Booking Confirmation Dialog - lazy loaded */}
      {isDeleteDialogOpen && (
        <Suspense fallback={null}>
          <ConfirmDialog
            isOpen={isDeleteDialogOpen}
            title="Slett booking"
            text="Er du sikker på at du vil slette bookingen?"
            confirmText="SLETT"
            cancelText="AVBRYT"
            confirmIcon="delete"
            destructive
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
            data-testid="delete-booking-dialog"
          />
        </Suspense>
      )}

      {/* Bulk Form Modal (Add/Edit/Delete from SelectionToolSidebar) */}
      {isBulkFormModalOpen && bulkFormMode && (
        <Modal
          isOpen={isBulkFormModalOpen}
          title={getBulkFormModalTitle()}
          onClose={handleBulkFormClose}
          closeOnOverlayClick={false}
          size="lg"
          data-testid="bulk-form-modal"
        >
          <ErrorBoundary
            FallbackComponent={(props) => (
              <FeatureErrorFallback
                {...props}
                title="Kunne ikke laste skjemaet"
                message="En feil oppstod ved lasting av skjemaet. Prøv igjen."
              />
            )}
            onReset={handleBulkFormClose}
          >
            <Suspense fallback={null}>
              <BookingFormWrapper
                mode={bulkFormMode}
                targetEntity={selectedTargetEntity}
                selectedCards={selectedCards}
                bookingType={
                  bookingType === BookingType.HARD_BOOKING ? "hard" : "soft"
                }
                absenceDisabled={absenceDisabled}
                onCancel={handleBulkFormClose}
                onDone={handleBulkFormDone}
                onError={handleBulkFormError}
                onClose={handleBulkFormClose}
                data-testid="bulk-form-wrapper"
              />
            </Suspense>
          </ErrorBoundary>
        </Modal>
      )}

      {/* Success Alert */}
      {successMessage && (
        <FlashAlert
          alert={{ message: successMessage, variant: "success" }}
          onDismiss={() => setSuccessMessage(null)}
        />
      )}

      {/* Error Alert */}
      {errorMessage && (
        <FlashAlert
          alert={{ message: errorMessage, variant: "error" }}
          onDismiss={() => setErrorMessage(null)}
        />
      )}
    </div>
  );
};

export default BookingPage;
